// Small inline editor for a stock's rank_in_list. Click to enter edit mode,
// Enter/blur to save, Esc to cancel. Persists via PATCH /api/stocks/{id}.
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function InlineRank({ stock, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(stock.rank_in_list != null ? String(stock.rank_in_list) : "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setVal(stock.rank_in_list != null ? String(stock.rank_in_list) : "");
  }, [stock.rank_in_list]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const stop = (e) => { if (e) { e.stopPropagation(); e.preventDefault(); } };

  const save = async () => {
    if (busy) return;
    const trimmed = val.trim();
    const parsed = trimmed === "" ? null : parseInt(trimmed, 10);
    if (parsed != null && (Number.isNaN(parsed) || parsed < 1)) {
      toast.error("Rang invalide");
      setEditing(false);
      setVal(stock.rank_in_list != null ? String(stock.rank_in_list) : "");
      return;
    }
    if (parsed === stock.rank_in_list) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/stocks/${stock.id}`, { rank_in_list: parsed });
      toast.success(`${stock.symbol} — rang ${parsed ?? "—"}`);
      onChanged?.(r.data);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setBusy(false); setEditing(false); }
  };

  const cancel = () => {
    setVal(stock.rank_in_list != null ? String(stock.rank_in_list) : "");
    setEditing(false);
  };

  if (editing) {
    return (
      <span onClick={stop} onMouseDown={stop} className="inline-flex items-center">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") { stop(e); save(); }
            if (e.key === "Escape") { stop(e); cancel(); }
          }}
          disabled={busy}
          className="w-10 h-6 text-xs font-mono-num text-center bg-input border border-monitor/40 rounded"
          data-testid={`rank-input-${stock.symbol}`}
        />
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { stop(e); setEditing(true); }}
      onKeyDown={(e) => { if (e.key === "Enter") { stop(e); setEditing(true); } }}
      className="font-mono-num text-xs sm:text-sm text-muted-2 cursor-text hover:text-app hover:underline decoration-dotted underline-offset-2"
      data-testid={`rank-view-${stock.symbol}`}
    >
      {stock.rank_in_list ?? "—"}
    </span>
  );
}
